import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { Download, Braces, Flame, AlertTriangle, TrendingUp, Award, HelpCircle } from "lucide-react";
import { useResults } from "../data/ResultsContext";
import RunSelector from "./RunSelector";
import { downloadChartSvg, downloadJson } from "../lib/chartExport";

/* Hoverable tooltip pill for chart explanations */
function MetricHint({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex ml-2 align-middle">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((s) => !s)}
        className="w-5 h-5 border border-white/20 hover:border-[#FF4D00]/60 flex items-center justify-center text-neutral-500 hover:text-[#FF4D00] transition-colors cursor-help"
        aria-label="Explain this metric"
      >
        <HelpCircle className="w-3 h-3" />
      </button>
      {show && (
        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 px-3 py-2.5 bg-[#0a0a0a] border border-[#FF4D00]/40 text-[11px] font-mono text-neutral-300 leading-relaxed z-50 shadow-[0_0_24px_rgba(255,77,0,0.15)] whitespace-normal not-italic">
          {text}
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] border-t border-l border-[#FF4D00]/40 rotate-45 mt-1" />
        </span>
      )}
    </span>
  );
}

const FIRE = "#FF4D00";
const CYAN = "#00F0FF";
const GREEN = "#3CCF7A";
const RED = "#E8503A";
const GRID = "#2a2a2a";
const AXIS = "#555";

function ChartCard({
  title,
  subtitle,
  children,
  testid,
  tone = "fire",
  exportData,
  exportName,
  nBadge,
}) {
  const toneColor = tone === "cyan" ? CYAN : tone === "green" ? GREEN : FIRE;
  const ref = useRef(null);
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7 }}
      data-testid={testid}
      ref={ref}
      className="h-full border border-white/10 bg-[#0d0d0d] p-6 lg:p-8 relative group hover:border-white/20 transition-colors flex flex-col z-10 hover:z-50"
    >
      <div
        className="absolute top-0 left-0 h-px w-0 group-hover:w-full transition-all duration-[900ms] ease-out"
        style={{ background: toneColor }}
      />
      <div className="flex items-baseline justify-between mb-2 gap-4 flex-wrap">
        <div className="font-mono text-[11px] tracking-[0.3em]" style={{ color: toneColor }}>
          {title}
        </div>
        <div className="flex items-center gap-3">
          {exportData && (
            <>
              <button
                onClick={() => downloadJson(exportData, `${exportName}.json`)}
                data-testid={`${testid}-json`}
                className="font-mono text-[10px] tracking-[0.22em] uppercase flex items-center gap-1.5 text-neutral-500 hover:text-[#00F0FF] transition-colors"
              >
                <Braces className="w-3 h-3" /> json
              </button>
              <button
                onClick={() => downloadChartSvg(ref.current, `${exportName}.png`)}
                data-testid={`${testid}-png`}
                className="font-mono text-[10px] tracking-[0.22em] uppercase flex items-center gap-1.5 text-neutral-500 hover:text-[#FF4D00] transition-colors"
              >
                <Download className="w-3 h-3" /> png
              </button>
            </>
          )}
          <div className="font-mono text-[10px] text-neutral-600 tracking-widest">
            {nBadge || `DEEP_PROBE · n=${results.meta.deep_probe_n}`}
          </div>
        </div>
      </div>
      {subtitle && (
        <div className="font-serif-display text-xl lg:text-2xl text-neutral-200 italic mb-6 leading-snug">
          {subtitle}
        </div>
      )}
      <div className="flex-1 flex flex-col min-h-0">
        {children}
      </div>
    </motion.div>
  );
}

function Legend({ items }) {
  return (
    <div className="mt-4 flex flex-wrap gap-4 text-[11px] font-mono text-neutral-500">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-2">
          <span className="w-3 h-3" style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function KeyMetric({ icon: Icon, label, value, sub, tone }) {
  const color = tone === "win" ? GREEN : tone === "loss" ? RED : FIRE;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      data-testid={`key-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="border border-white/10 bg-[#0d0d0d] p-5 relative overflow-hidden hover:border-white/25 transition-colors z-10 hover:z-50"
    >
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div
        className="font-serif-display text-4xl lg:text-5xl"
        style={{ color, textShadow: `0 0 18px ${color}55` }}
      >
        {value}
      </div>
      <div className="mt-2 text-[11px] font-mono text-neutral-500 leading-relaxed">
        {sub}
      </div>
    </motion.div>
  );
}

const METRIC_CONFIG = {
  opus_4_7_at_scale: { icon: TrendingUp, label: "Opus 4.7 at scale" },
  gpt_5_4_mini_surprise: { icon: Award, label: "GPT-5.4 Mini Surprise" },
  haiku_4_5_debut: { icon: Flame, label: "Haiku 4.5 Debut" },
  gpt_5_4_last: { icon: AlertTriangle, label: "GPT-5.4 Dead Last" },
  gpt_5_4_at_scale: { icon: AlertTriangle, label: "GPT-5.4 at scale" },
  opus_4_7_adversarial: { icon: Flame, label: "Opus 4.7 adversarial" },
  v3_2_underdetermined: { icon: Award, label: "V3.2 UNDERDETERMINED" }
};

export default function LatestFindings() {
  const { results, isArchive } = useResults();
  const m = results.key_metrics;

  // Scale Validity Gap — all 4 deep_probe models, grouped bars (curated vs scale)
  const scaleGapData = results.scale_validity_gap_all.map((d) => ({
    model: d.model,
    curated_200: d.curated,
    scale_1459: d.scale,
    delta: d.delta,
  }));

  // Adversarial collapse — paired T1 / T3 per model
  const advData = results.adversarial_t1_t3.map((d) => ({
    model: d.model,
    turn1: d.t1 * 100,
    turn3: d.t3 * 100,
    delta: d.delta,
  }));

  // Probe accuracy — UNDER / CONTRA / Overall per model
  const probeData = results.probe_accuracy.map((d) => ({
    model: d.model,
    underdetermined: d.under * 100,
    contradictory: d.contra * 100,
    overall: d.overall * 100,
  }));

  // ECI leaderboard — DEEP_PROBE models only
  const leaderboard = results.models
    .filter((x) => x.benchmark_mode === "DEEP_PROBE")
    .map((x) => ({ name: x.short, eci: x.eci }))
    .sort((a, b) => b.eci - a.eci);

  return (
    <section
      id="findings"
      data-testid="findings-section"
      className="relative py-32 px-6 lg:px-10 border-t border-white/5 bg-[#070707]"
    >
      <div className="absolute inset-0 tech-grid grid-drift opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="chapter-label">02 · Latest Findings</div>
            <RunSelector />
          </div>
          <h2
            data-testid="findings-headline"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-neutral-100 max-w-5xl"
          >
            {results.meta.headline}
          </h2>
          <p className="mt-5 font-serif-display italic text-lg lg:text-xl text-neutral-400 max-w-4xl leading-snug">
            {results.meta.subheadline}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs font-mono text-neutral-500 tracking-widest">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#FF4D00] pulse-dot" />
              RUN · {results.meta.run_date}
            </span>
            <span className="text-neutral-700">|</span>
            <span>{results.meta.data_source}</span>
          </div>
        </motion.div>

        {/* Key metric tiles — the WOW bar */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {Object.entries(m).map(([key, metric]) => {
            const config = METRIC_CONFIG[key] || { icon: TrendingUp, label: key.replace(/_/g, " ") };
            const Icon = config.icon;
            const displayValue = metric.format === "percent" 
              ? `${Math.round(metric.value * 100)}%` 
              : Number(metric.value).toFixed(3);

            return (
              <KeyMetric
                key={key}
                icon={Icon}
                label={config.label}
                value={displayValue}
                sub={metric.label}
                tone={metric.tone}
              />
            );
          })}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Scale Validity Gap — all 5 deep_probe models */}
          <div className="lg:col-span-7">
            <ChartCard
              title="SCALE · VALIDITY · GAP · ALL · 5 · DEEP_PROBE · MODELS"
              subtitle={<>Every model's ECI drops when scaled from 200 curated items to 1,459. Universal.<MetricHint text="ECI (Epistemic Calibration Index) measures how well a model knows what it knows. The Scale Validity Gap shows that ECI scores on small curated sets overstate real performance." /></>}
              testid="chart-scale-gap"
              exportData={results.scale_validity_gap_all}
              exportName="prometheus-ebm_scale-validity-gap"
              nBadge="5 MODELS · CURATED vs SCALE"
            >
              <div className="flex-1 min-h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={scaleGapData}
                    margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                  >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="model"
                      stroke={AXIS}
                      tick={{ fill: "#a3a3a3", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0.55, 0.92]}
                      stroke={AXIS}
                      tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(v) => Number(v).toFixed(3)}
                      cursor={{ fill: "rgba(0,240,255,0.05)" }}
                    />
                    <Bar dataKey="curated_200" fill={CYAN} animationDuration={1300} name="200-item (curated)" />
                    <Bar dataKey="scale_1459" fill={FIRE} animationDuration={1600} name="1459-item (scale)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Legend
                items={[
                  { label: "200-item curated (overstates)", color: CYAN },
                  { label: "1,459-item scale (truth)", color: FIRE },
                ]}
              />
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px] font-mono">
                {results.scale_validity_gap_all.map((d) => (
                  <div key={d.model} className="border border-white/10 px-3 py-2">
                    <div className="text-neutral-400">{d.model}</div>
                    <div className="text-[#E8503A] mt-0.5">Δ {d.delta.toFixed(3)}</div>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          {/* ECI Leaderboard at scale */}
          <div className="lg:col-span-5">
            <ChartCard
              title="ECI · SCALE · LEADERBOARD"
              subtitle={<>Opus 4.7 leads. Sonnet 4.6 overtakes Opus 4.6. GPT-5.4 ranks last.<MetricHint text="ECI at full scale (n=1,459). The cyan line at 0.85 is the AGI metacognition threshold — a model that crosses it knows what it knows well enough to self-correct toward general intelligence." /></>}
              tone="cyan"
              testid="chart-eci"
              exportData={leaderboard}
              exportName="prometheus-ebm_eci-leaderboard"
              nBadge={`${leaderboard.length} MODELS`}
            >
              <div className="flex-1 min-h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={leaderboard}
                    layout="vertical"
                    margin={{ top: 25, right: 30, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid stroke={GRID} horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 1]}
                      stroke={AXIS}
                      tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke={AXIS}
                      tick={{ fill: "#a3a3a3", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                      width={110}
                    />
                    <Tooltip formatter={(v) => Number(v).toFixed(3)} />
                    <ReferenceLine
                      x={0.85}
                      stroke="#00F0FF"
                      strokeOpacity={0.4}
                      strokeDasharray="2 5"
                      label={{
                        value: "◆ 0.85 · AGI METACOGNITION LINE",
                        fill: "#00F0FF",
                        fillOpacity: 0.6,
                        fontSize: 8,
                        position: "top",
                        fontFamily: "JetBrains Mono",
                      }}
                    />
                    <Bar dataKey="eci" animationDuration={1400}>
                      {leaderboard.map((d, i) => (
                        <Cell
                          key={i}
                          fill={d.eci >= 0.85 ? FIRE : "#b03a00"}
                          stroke={d.eci >= 0.85 ? FIRE : "#b03a00"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Legend
                items={[
                  { label: "DEEP_PROBE n=1459", color: FIRE },
                ]}
              />
            </ChartCard>
          </div>

          {/* Adversarial T1 → T3 (real!) */}
          <div className="lg:col-span-7">
            <ChartCard
              title="ADVERSARIAL · T1 · vs · T3 · COLLAPSE"
              subtitle={<>Single false-authority challenge. Opus 4.6 improves 75%→80% — the best adversarial resilience yet.<MetricHint text="Adversarial T1→T3: the model is told by a 'credible expert' that its answer is wrong. T1 = original accuracy, T3 = accuracy after the challenge. A big drop means the model caves to pressure." /></>}
              testid="chart-adversarial"
              exportData={results.adversarial_t1_t3}
              exportName="prometheus-ebm_adversarial-t1-t3"
              nBadge={`n=${results.meta.adversarial_n} × 2 TURNS · 6 MODELS`}
            >
              <div className="h-[380px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={advData}
                    margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                  >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="model"
                      stroke={AXIS}
                      tick={{ fill: "#a3a3a3", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke={AXIS}
                      tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                      tickFormatter={(v) => v + "%"}
                    />
                    <Tooltip formatter={(v) => Math.round(v) + "%"} />
                    <Bar dataKey="turn1" fill={FIRE} animationDuration={1300} name="Turn 1 (baseline)" />
                    <Bar dataKey="turn3" fill={CYAN} animationDuration={1600} name="Turn 3 (after probe)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Legend
                items={[
                  { label: "Turn 1 · baseline", color: FIRE },
                  { label: "Turn 3 · after adversarial probe", color: CYAN },
                ]}
              />
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2 text-[10px] font-mono">
                {advData.map((d) => {
                  const color =
                    d.delta <= -0.25 ? "#E8503A" : d.delta < 0 ? "#FF9040" : d.delta > 0 ? GREEN : "#737373";
                  return (
                    <div key={d.model} className="border border-white/10 px-2 py-1.5">
                      <div className="text-neutral-500 truncate">{d.model}</div>
                      <div style={{ color }}>
                        {d.delta > 0 ? "+" : ""}
                        {Math.round(d.delta * 100)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </ChartCard>
          </div>

          {/* Probe Accuracy */}
          <div className="lg:col-span-5">
            <ChartCard
              title="PROBE · ACCURACY · UNDER · vs · CONTRA"
              subtitle={<>V3.2 holds the 80% UNDERDETERMINED record — the best ambiguity detector ever tested.<MetricHint text="Probe accuracy on two hard question types: UNDERDETERMINED (not enough info to answer) and CONTRADICTORY (premises conflict). A good model should abstain rather than guess." /></>}
              tone="cyan"
              testid="chart-probe"
              exportData={results.probe_accuracy}
              exportName="prometheus-ebm_probe-accuracy"
              nBadge="MULTI-RUN 6 · n=324"
            >
              <div className="h-[380px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={probeData}
                    margin={{ top: 20, right: 20, left: 0, bottom: 30 }}
                  >
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="model"
                      stroke={AXIS}
                      tick={{ fill: "#a3a3a3", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                      angle={-15}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke={AXIS}
                      tick={{ fill: "#737373", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={{ stroke: GRID }}
                      tickLine={false}
                      tickFormatter={(v) => v + "%"}
                    />
                    <Tooltip formatter={(v) => Math.round(v) + "%"} />
                    <Bar dataKey="underdetermined" fill="#2B6CB0" animationDuration={1200} name="UNDERDETERMINED" />
                    <Bar dataKey="contradictory" fill={GREEN} animationDuration={1400} name="CONTRADICTORY" />
                    <Bar dataKey="overall" fill="#C07530" animationDuration={1600} name="Overall" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Legend
                items={[
                  { label: "UNDERDETERMINED", color: "#2B6CB0" },
                  { label: "CONTRADICTORY", color: GREEN },
                  { label: "Overall", color: "#C07530" },
                ]}
              />
            </ChartCard>
          </div>
        </div>
      </div>
    </section>
  );
}
